import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import style from "./AviationExpeditorActionsTab.module.scss";
import AviationExpeditorBidForm from "./AviationExpeditorBidForm";
import AviationExpeditorBidBlock from "./AviationExpeditorBidBlock";
import AviationExpeditorTenderClosedInfo from "./AviationExpeditorTenderClosedInfo";
import AviationExpeditorStatusAction from "./AviationExpeditorStatusAction";
import AviationExpeditorAssignedAction from "./AviationExpeditorAssignedAction";
import AviationExpeditorExpectedArrivalBlock from "./AviationExpeditorExpectedArrivalBlock";
import AviationExpeditorDocumentUpload from "./AviationExpeditorDocumentUpload";
import AviationExpeditorReturnAction from "./AviationExpeditorReturnAction";
import AviationExpeditorFinalStatusInfo from "./AviationExpeditorFinalStatusInfo";
import CanceledOrderCard from "../../../../orders-new/order-info-expeditor/components/CanceledOrderCard/CanceledOrderCard";
import ExpeditorRejectedOrderCard from "../../../../orders-new/order-info-expeditor/components/ExpeditorRejectedOrderCard/ExpeditorRejectedOrderCard";

const ACTION_STATUSES = new Set(["confirmed", "accepted", "onthetrip"]);

const AviationExpeditorActionsTab = ({
  order,
  tenderState,
  fetchOrderData,
  onForceUpdate,
  refreshDocuments,
  onClose,
  history = [],
}) => {
  const { t } = useTranslation();
  const userCompanyId = useSelector((state) => state.user.companyId ?? state.user.company?.id);
  const {
    previousBids,
    bidsLoaded,
    loadPreviousBids,
    isTenderEnded,
    isOtherExpeditorAssigned,
    showBidBlock,
    showTenderClosedInfo,
  } = tenderState;
  const [expectedArrivalError, setExpectedArrivalError] = useState("");
  const [expectedArrivalFocus, setExpectedArrivalFocus] = useState({ orderId: null, request: 0 });

  const status = String(order?.status || "").toLowerCase();
  const isExpeditorRejected = status === "expeditorrejected";
  const isCanceled = status === "canceled";
  const canceledStatus = [...(Array.isArray(history) ? history : [])]
    .reverse()
    .find((item) => String(item?.statusCode || "").toLowerCase() === "canceled");
  const canceledText = `${t("orderInfoExpeditor.history.statusCanceledByPartner")}. ${t(
    "orderInfoExpeditor.history.priceChangedReason",
  )} ${canceledStatus?.comment?.trim() || "не указана"}`;
  useEffect(() => {
    setExpectedArrivalError("");
  }, [order?.id, order?.expectedUnloadingDate]);

  const handleExpectedArrivalError = (errorText) => {
    setExpectedArrivalError(errorText);
    if (errorText) {
      setExpectedArrivalFocus((previous) => ({ orderId: order.id, request: previous.request + 1 }));
    }
  };

  const handleBidUpdate = async () => {
    await loadPreviousBids();
    onForceUpdate?.();
  };

  if (!order) return null;

  if (
    isExpeditorRejected &&
    userCompanyId != null &&
    order.expeditorId != null &&
    String(userCompanyId) === String(order.expeditorId)
  ) {
    return (
      <div className={style.wrapper}>
        <ExpeditorRejectedOrderCard
          history={history}
          updatedAt={order.updatedAt}
          onFindNewOrders={onClose}
        />
      </div>
    );
  }

  const hasPreviousBid = previousBids.length > 0;

  const isDirectAssignment = order.assignmentMethod === "DirectAssignment";
  const tenderContent = isDirectAssignment ? null : showBidBlock ? (
    <AviationExpeditorBidBlock
      onRefreshOrder={fetchOrderData}
      bid={previousBids[0]}
      order={order}
      isCanceled={isCanceled}
      canceledText={canceledText}
      isOtherExpeditorAssigned={isOtherExpeditorAssigned}
      onUpdateData={handleBidUpdate}
      onFindNewOrders={onClose}
    />
  ) : showTenderClosedInfo ? (
    <AviationExpeditorTenderClosedInfo assignmentMethod={order.assignmentMethod} />
  ) : null;

  if (tenderState.view === "tender" || (isCanceled && showBidBlock)) {
    return <div className={style.wrapper}>{tenderContent}</div>;
  }

  if (isCanceled) {
    if (!bidsLoaded) return <div className={style.wrapper} />;
    return (
      <div className={style.wrapper}>
        <CanceledOrderCard
          canceledStatus={canceledStatus}
          emptyReasonText="не указана"
          onFindNewOrders={onClose}
        />
      </div>
    );
  }

  const showBidForm =
    !isDirectAssignment && status === "awaitingoffer" && !isTenderEnded && bidsLoaded && !hasPreviousBid;
  return (
    <div className={style.wrapper}>
      {tenderContent}
      {showBidForm ? (
        <AviationExpeditorBidForm onRefreshOrder={fetchOrderData} order={order} onUpdateData={handleBidUpdate} />
      ) : null}

      {status === "assigned" && !isOtherExpeditorAssigned ? (
        <AviationExpeditorAssignedAction
          key={order.id}
          order={order}
          fetchOrderData={fetchOrderData}
          onForceUpdate={onForceUpdate}
        />
      ) : null}

      {ACTION_STATUSES.has(status) && !isOtherExpeditorAssigned ? (
        <AviationExpeditorStatusAction
          order={order}
          fetchOrderData={fetchOrderData}
          onForceUpdate={onForceUpdate}
          onExpectedArrivalError={handleExpectedArrivalError}
        />
      ) : null}

      {status === "return" && !isOtherExpeditorAssigned ? (
        <AviationExpeditorReturnAction
          order={order}
          fetchOrderData={fetchOrderData}
          onForceUpdate={onForceUpdate}
        />
      ) : null}

      {!isOtherExpeditorAssigned ? (
        <>
          <AviationExpeditorExpectedArrivalBlock
            key={order.id}
            order={order}
            fetchOrderData={fetchOrderData}
            errorText={expectedArrivalError}
            onErrorChange={setExpectedArrivalError}
            focusRequest={
              expectedArrivalFocus.orderId === order.id && !order.expectedUnloadingDate
                ? expectedArrivalFocus.request
                : 0
            }
          />

          {!isExpeditorRejected ? (
            <AviationExpeditorDocumentUpload
              order={order}
              refreshDocuments={refreshDocuments}
            />
          ) : null}
        </>
      ) : null}

      <AviationExpeditorFinalStatusInfo order={order} />
    </div>
  );
};

export default AviationExpeditorActionsTab;
